import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // バリデーション
    const company = formData.get('company') as string;
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const phone = formData.get('phone') as string;
    
    if (!company || !name || !email || !phone) {
      alert('すべての項目を入力してください');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d60n67g3qj6k6vba3sig', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setShowSuccess(true);
        form.reset();
        setTimeout(() => setShowSuccess(false), 5000);
      } else {
        alert('送信に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      alert('送信に失敗しました。もう一度お試しください。');
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img src="https://static.readdy.ai/image/0a1299d4bb93b406ab47dbe2ff653617/79d78232fa5725ce1d90cdbe2a4f46fa.png" alt="BOOOSTER" className="h-20" />
          </div>
          <div className="flex items-center gap-8">
            <a href="#service" className={`text-base font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white hover:text-teal-300'}`}>サービス</a>
            <a href="#results" className={`text-base font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white hover:text-teal-300'}`}>事例</a>
            <a href="#diagnosis" className={`text-base font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white hover:text-teal-300'}`}>診断</a>
            <a href="#contact" className={`text-base font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white hover:text-teal-300'}`}>お問い合わせ</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/28d5197b1f5516bc1b7ed6d48902100c.jpg" 
            alt="Hero Background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3 text-white">
            <div className="inline-block bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <span className="text-lg font-medium text-white">BOOOSTER</span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-bold leading-tight mb-6">
              代理店は<span className="text-white">"集める"ものじゃない。</span><span className="text-teal-400">"動かす"ものだ。</span>
            </h1>
            <p className="text-2xl text-gray-200 mb-8 leading-relaxed max-w-2xl">
              代理店は増えたのに、売上は思ったほど伸びていない。<br />
              そんな経験、ありませんか？
            </p>
            <div className="flex items-center gap-6">
              <a href="#diagnosis" className="inline-block bg-teal-500 hover:bg-teal-600 text-white font-bold px-10 py-4 rounded-full transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap">
                無料で代理店相性を診断する
              </a>
              <a href="#service" className="text-white hover:text-teal-300 font-medium transition-colors whitespace-nowrap">
                詳しく見る →
              </a>
            </div>
          </div>
          
          <div className="lg:col-span-2">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://readdy.ai/api/search-image?query=Solitary%20business%20executive%20viewing%20documents%20and%20charts%20alone%20from%20behind%2C%20contemplative%20mood%20in%20modern%20office%2C%20soft%20window%20lighting%2C%20professional%20attire%2C%20strategic%20planning%20atmosphere%2C%20muted%20color%20palette%20with%20deep%20blues%2C%20cinematic%20composition%2C%20business%20introspection%20moment&width=800&height=1000&seq=hero-img-002&orientation=portrait" 
                alt="経営者の後ろ姿" 
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block bg-gray-100 text-gray-600 text-sm font-semibold px-4 py-2 rounded-full mb-6">PROBLEM</span>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-8 leading-tight">
              代理店構築で、<br />こんな違和感を感じていませんか？
            </h2>
          </div>
          
          <div className="mb-12 rounded-2xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=Empty%20corporate%20meeting%20room%20with%20whiteboard%20covered%20in%20unfinished%20strategy%20notes%20and%20diagrams%2C%20abandoned%20coffee%20cups%20on%20table%2C%20late%20afternoon%20lighting%20through%20windows%2C%20sense%20of%20stalled%20progress%2C%20professional%20office%20interior%2C%20muted%20colors%20with%20gray%20tones%2C%20realistic%20business%20photography&width=1200&height=600&seq=problem-img-003&orientation=landscape" 
              alt="会議室で止まったホワイトボード" 
              className="w-full h-96 object-cover"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white border border-gray-200 rounded-xl p-6 border-l-4 border-l-teal-500">
              <p className="text-gray-700 italic text-lg">"最初はやる気があったのに、いつの間にか放置"</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 border-l-4 border-l-teal-500">
              <p className="text-gray-700 italic text-lg">"結局、直販の方が早い気がしてきた"</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 border-l-4 border-l-teal-500">
              <p className="text-gray-700 italic text-lg">"商材が悪いのか、やり方が悪いのかわからない"</p>
            </div>
          </div>
          
          <p className="text-center text-gray-500 text-lg">その悩み、あなただけではありません。</p>
        </div>
      </section>

      {/* Solution Section */}
      <section id="service" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <div className="relative rounded-3xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Collapsed%20architectural%20blueprint%20and%20broken%20building%20blocks%20scattered%20on%20desk%2C%20failed%20design%20concept%20visualization%2C%20dramatic%20lighting%20highlighting%20disorder%2C%20professional%20workspace%20setting%2C%20cool%20blue%20and%20gray%20tones%2C%20metaphor%20for%20flawed%20planning%2C%20high%20contrast%20photography&width=800&height=800&seq=solution-img-004&orientation=squarish" 
                  alt="崩れた設計図" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-6 right-6 bg-white px-4 py-2 rounded-full">
                  <span className="text-sm font-semibold text-gray-700">SOLUTION</span>
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-3">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                問題は"代理店"ではなく、<br />"設計"にあります
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                代理店が動かない理由は、気合や熱量の問題ではありません。<br /><br />
                商材選定、報酬設計、初動の設計。<br />
                ここを間違えると、どんなに集めても成果は出ません。
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-3 h-3 bg-teal-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 font-medium text-lg">商材選定の精度</p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-3 h-3 bg-teal-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 font-medium text-lg">報酬設計の最適化</p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-3 h-3 bg-teal-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 font-medium text-lg">初動フェーズの伴走</p>
                </div>
              </div>
              
              <a href="#benefits" className="text-teal-500 hover:text-teal-600 font-medium transition-colors">
                詳しく見る →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Unique Value Section */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight">
            BOOOSTER は、<br />すべての商材を引き受けません
          </h2>
          <p className="text-lg text-gray-300 leading-relaxed mb-12 max-w-3xl mx-auto">
            BOOOSTER は、代理店モデルとして成功確率が高い商材のみを支援します。<br />
            しかもその判断は、一般的な成功論ではなく、<br />
            私たち自身の過去実績データが基準です。
          </p>
          
          <div className="border-2 border-white rounded-3xl p-8 bg-transparent backdrop-blur-sm flex flex-col md:flex-row items-center justify-center gap-8 mb-12">
            <div className="w-16 h-16 flex items-center justify-center">
              <i className="ri-shield-check-line text-5xl text-teal-400"></i>
            </div>
            <p className="text-2xl font-bold text-white">だからこそ、本気で伴走できます。</p>
          </div>
          
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=Professional%20business%20team%20engaged%20in%20collaborative%20discussion%20around%20conference%20table%2C%20diverse%20group%20analyzing%20documents%20together%2C%20bright%20modern%20office%20with%20natural%20light%2C%20teamwork%20and%20partnership%20atmosphere%2C%20warm%20professional%20tones%20with%20teal%20accents%2C%20authentic%20corporate%20photography%20style&width=1200&height=500&seq=value-img-005&orientation=landscape" 
              alt="チームで議論" 
              className="w-full h-64 object-cover"
            />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">3つのベネフィット</h2>
            <div className="w-24 h-1 bg-teal-500 rounded-full"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Business%20training%20workshop%20with%20presenter%20and%20engaged%20audience%20taking%20notes%2C%20professional%20seminar%20setting%2C%20bright%20conference%20room%20with%20projector%20screen%2C%20educational%20atmosphere%2C%20participants%20focused%20on%20learning%2C%20clean%20modern%20interior%20with%20teal%20accent%20lighting&width=600&height=400&seq=benefit-img-006&orientation=landscape" 
                  alt="代理店向け勉強会" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
                <span className="inline-block bg-teal-100 text-teal-700 text-sm font-semibold px-3 py-1 rounded-full mb-4">稼働率向上</span>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">動く代理店だけが残る</h3>
                <p className="text-gray-600 leading-relaxed text-base">
                  マニュアル作成から初期稼働まで同席。<br />
                  「作ったけど動かない」を防ぎます。
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="h-64 bg-gray-900 flex items-center justify-center overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20growth%20chart%20with%20ascending%20line%20graph%20in%20teal%20color%20on%20dark%20background%2C%20simple%20data%20visualization%20showing%20business%20expansion%2C%20geometric%20shapes%20and%20grid%20pattern%2C%20modern%20infographic%20style%2C%20professional%20business%20analytics%20aesthetic&width=600&height=400&seq=benefit-img-007&orientation=landscape" 
                  alt="成長曲線" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
                <span className="inline-block bg-teal-100 text-teal-700 text-sm font-semibold px-3 py-1 rounded-full mb-4">再現性</span>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">再現性のある拡大モデル</h3>
                <p className="text-gray-600 leading-relaxed text-base">
                  感覚ではなく、型で広げる。<br />
                  だから1年後も続きます。
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Close-up%20of%20two%20business%20professionals%20shaking%20hands%20in%20agreement%2C%20formal%20business%20attire%2C%20natural%20office%20lighting%2C%20trust%20and%20partnership%20symbolism%2C%20professional%20corporate%20handshake%2C%20shallow%20depth%20of%20field%20focusing%20on%20hands%2C%20warm%20professional%20tones&width=600&height=400&seq=benefit-img-008&orientation=landscape" 
                  alt="握手" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
                <span className="inline-block bg-teal-100 text-teal-700 text-sm font-semibold px-3 py-1 rounded-full mb-4">成果保証</span>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">成果に連動したフェアな設計</h3>
                <p className="text-gray-600 leading-relaxed text-base">
                  成果に届かなければ返金保証。<br />
                  本気で成果を出しにいきます。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section id="results" className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block bg-gray-200 text-gray-600 text-sm font-semibold px-4 py-2 rounded-full mb-6">RESULTS</span>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-12">実際にこんな成果が出ています</h2>
          </div>
          
          <div className="space-y-8 mb-8">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 flex items-center justify-center flex-shrink-0">
                <i className="ri-check-double-line text-5xl text-teal-500"></i>
              </div>
              <div>
                <p className="text-2xl text-gray-800 font-medium">開始3年で300社のパートナー契約</p>
                <p className="text-lg text-gray-600 mt-1">パートナー経由の受注件数 47社</p>
              </div>
            </div>
            
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 flex items-center justify-center flex-shrink-0">
                <i className="ri-line-chart-line text-5xl text-teal-500"></i>
              </div>
              <div>
                <p className="text-2xl text-gray-800 font-medium">税理士パートナー8社と契約</p>
                <p className="text-lg text-gray-600 mt-1">1600万円/3件の受注</p>
              </div>
            </div>
            
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 flex items-center justify-center flex-shrink-0">
                <i className="ri-rocket-line text-5xl text-teal-500"></i>
              </div>
              <div>
                <p className="text-2xl text-gray-800 font-medium">直販依存からの脱却に成功</p>
              </div>
            </div>
          </div>
          
          <p className="text-center text-gray-400 text-base mb-8">※一部事例抜粋</p>
        </div>
      </section>

      {/* CTA Section */}
      <section id="diagnosis" className="py-24 bg-gradient-to-br from-teal-500 to-teal-600">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-gray-400 text-base mb-4 block">最後に</span>
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight">
              代理店構築は、<br />
              一度失敗すると怖くなります。<br />
              だからこそ、<br />
              次は「成功前提」で設計しませんか。
            </h2>
          </div>
          
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">代理店相性診断フォーム</h3>
            
            {showSuccess && (
              <div className="mb-6 bg-teal-50 border border-teal-200 text-teal-700 px-6 py-4 rounded-xl text-base">
                送信が完了しました。担当者より3営業日以内にご連絡いたします。
              </div>
            )}
            
            <form id="diagnosis-form" data-readdy-form onSubmit={handleFormSubmit} className="space-y-6">
              <div>
                <label htmlFor="company" className="block text-base font-semibold text-gray-700 mb-2">会社名 <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  id="company" 
                  name="company" 
                  required 
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-base"
                  placeholder="株式会社〇〇"
                />
              </div>
              
              <div>
                <label htmlFor="name" className="block text-base font-semibold text-gray-700 mb-2">お名前 <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  required 
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-base"
                  placeholder="山田 太郎"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-base font-semibold text-gray-700 mb-2">メールアドレス <span className="text-red-500">*</span></label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  required 
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-base"
                  placeholder="example@company.co.jp"
                />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-base font-semibold text-gray-700 mb-2">電話番号 <span className="text-red-500">*</span></label>
                <input 
                  type="tel" 
                  id="phone" 
                  name="phone" 
                  required 
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-base"
                  placeholder="03-1234-5678"
                />
              </div>
              
              <div>
                <label htmlFor="product" className="block text-base font-semibold text-gray-700 mb-2">商材・サービス内容</label>
                <textarea 
                  id="product" 
                  name="product" 
                  rows={4} 
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-base resize-none"
                  placeholder="現在提供している商材やサービスについて簡単にご記入ください（500文字以内）"
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">500文字以内でご入力ください</p>
              </div>
              
              <div>
                <label htmlFor="challenge" className="block text-base font-semibold text-gray-700 mb-2">現在の課題</label>
                <textarea 
                  id="challenge" 
                  name="challenge" 
                  rows={4} 
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-base resize-none"
                  placeholder="代理店構築に関する課題や悩みをお聞かせください（500文字以内）"
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">500文字以内でご入力ください</p>
              </div>
              
              <button 
                type="submit" 
                className="w-full bg-teal-500 hover:bg-teal-600 text-white font-bold px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap"
              >
                診断を申し込む
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </form>
            
            <p className="text-center text-gray-500 text-base mt-6">所要時間：約3分</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
            <div className="lg:col-span-2">
              <img src="https://static.readdy.ai/image/0a1299d4bb93b406ab47dbe2ff653617/79d78232fa5725ce1d90cdbe2a4f46fa.png" alt="BOOOSTER" className="h-10 mb-6" />
              <p className="text-gray-400 text-base leading-relaxed mb-6">
                代理店構築を成功前提で設計する。<br />
                BOOOSTER は、動く代理店を作ります。
              </p>
            </div>
            
            <div>
              <h4 className="font-bold mb-4 text-base">サービス</h4>
              <ul className="space-y-2 text-base">
                <li><a href="#service" className="text-gray-400 hover:text-teal-400 transition-colors">代理店構築支援</a></li>
                <li><a href="#benefits" className="text-gray-400 hover:text-teal-400 transition-colors">ベネフィット</a></li>
                <li><a href="#results" className="text-gray-400 hover:text-teal-400 transition-colors">実績</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-4 text-base">会社情報</h4>
              <ul className="space-y-2 text-base">
                <li><a href="#" className="text-gray-400 hover:text-teal-400 transition-colors">会社概要</a></li>
                <li><a href="#" className="text-gray-400 hover:text-teal-400 transition-colors">お問い合わせ</a></li>
                <li><a href="#" className="text-gray-400 hover:text-teal-400 transition-colors">プライバシーポリシー</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-4 text-base">リソース</h4>
              <ul className="space-y-2 text-base">
                <li><a href="#diagnosis" className="text-gray-400 hover:text-teal-400 transition-colors">相性診断</a></li>
                <li><a href="#" className="text-gray-400 hover:text-teal-400 transition-colors">よくある質問</a></li>
                <li><a href="#" className="text-gray-400 hover:text-teal-400 transition-colors">ブログ</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-base">© 2025 BOOOSTER. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition-colors text-base">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
